import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Button from '../common/Button';
import ModernLanguageSwitcher from '../common/ModernLanguageSwitcher';
import NotificationBell from '../common/NotificationBell';
import NotificationDropdown from '../common/NotificationDropdown';
import { scrollToTop } from '../../utils/scrollToTop';
import {
  Bars3Icon,
  XMarkIcon,
  ShoppingCartIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { user, isAuthenticated, logout, cartCount } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navigation = [
    { name: t('nav.home', 'Home'), href: '/' },
    { name: t('nav.about', 'About'), href: '/about' },
    { name: t('nav.products', 'Products'), href: '/products' },
    { name: t('nav.news', 'News'), href: '/news' },
    { name: t('nav.contact', 'Contact'), href: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Handle scroll for background effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
  };

  return (
    <header 
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/98 shadow-lg backdrop-blur-md' : 'bg-white/95 shadow-md'
      } border-b border-gray-200`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <nav className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" aria-label={t('nav.mainNavigation', 'Main navigation')}>
        <div className="flex items-center justify-between h-16 md:h-18 lg:h-20">
          
          {/* Logo Section */}
          <div className="flex-shrink-0">
            <Link 
              to="/" 
              onClick={scrollToTop} 
              className="flex items-center group focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg"
              aria-label={t('nav.home', 'Home')}
            >
              <div className="relative">
                <img
                  src="/logo.png"
                  alt="RFBOX company"
                  className="h-10 w-auto sm:h-12 lg:h-14 xl:h-16 transition-transform duration-200 group-hover:scale-105"
                />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation - Hidden on mobile/tablet */}
          <div className="hidden md:flex flex-1 justify-center">
            <div className={`flex items-center ${isRTL ? 'gap-x-reverse' : ''} gap-x-3 lg:gap-x-4`}>
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={scrollToTop}
                  className={`relative px-2.5 lg:px-3 py-2 rounded-lg text-xs lg:text-sm font-medium transition-all duration-200 whitespace-nowrap group focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    isActive(item.href)
                      ? 'text-primary-600 bg-primary-50 shadow-sm'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-primary-50/80'
                  }`}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                >
                  {item.name}
                  {isActive(item.href) && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 rounded-full" aria-hidden="true"></span>
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center flex-shrink-0">
            
            {/* Desktop Actions - Hidden on mobile */}
            <div className={`hidden md:flex items-center ${isRTL ? 'gap-x-reverse' : ''} gap-x-2 lg:gap-x-3`}>
              
              {/* Notification Bell - Desktop */}
              {isAuthenticated && (
                <div className="relative">
                  <NotificationBell 
                    onClick={() => setIsNotifOpen(v => !v)}
                    aria-label={t('notifications.title', 'Notifications')}
                  />
                  <NotificationDropdown 
                    isOpen={isNotifOpen} 
                    onClose={() => setIsNotifOpen(false)} 
                  />
                </div>
              )}
              
              {/* Cart Icon - Desktop */}
              <button 
                onClick={() => { navigate('/cart'); scrollToTop(); }}
                className="relative p-2 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label={`${t('cart.title', 'Cart')} (${cartCount} ${t('cart.items', 'items')})`}
              >
                <ShoppingCartIcon className="h-5 w-5 lg:h-5.5 lg:w-5.5 group-hover:scale-110 transition-transform duration-200" />
                {cartCount > 0 && (
                  <span 
                    className={`absolute -top-1 ${isRTL ? '-left-1' : '-right-1'} bg-primary-500 text-white text-[10px] lg:text-xs rounded-full h-4.5 min-w-[18px] lg:h-5 lg:min-w-[20px] px-1 flex items-center justify-center font-semibold shadow-md`}
                    aria-hidden="true"
                  >
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </button>
              
              {/* Language Switcher - Desktop */}
              <div className="flex items-center">
                <ModernLanguageSwitcher />
              </div>
              
              {/* Auth Buttons - Desktop */}
              {isAuthenticated ? (
                <div className={`flex items-center ${isRTL ? 'gap-x-reverse' : ''} gap-x-1.5 lg:gap-x-2`}>
                  {user?.role === 'admin' && (
                    <Link to="/admin" onClick={scrollToTop}>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-xs px-2.5 lg:px-3 py-1.5 lg:py-2 whitespace-nowrap"
                      >
                        {t('nav.admin', 'Admin')}
                      </Button>
                    </Link>
                  )}
                  {user?.role !== 'admin' && (
                    <Link to="/profile" onClick={scrollToTop}>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-xs px-2.5 lg:px-3 py-1.5 lg:py-2 whitespace-nowrap flex items-center"
                      >
                        <UserIcon className={`h-3.5 w-3.5 lg:h-4 lg:w-4 ${isRTL ? 'ml-1.5' : 'mr-1.5'}`} />
                        <span className="hidden lg:inline max-w-[80px] xl:max-w-[120px] truncate">
                          {user?.firstName || t('nav.profile', 'Profile')}
                        </span>
                        <span className="lg:hidden">
                          {(user?.firstName || t('nav.profile', 'Profile')).substring(0, 6)}
                        </span>
                      </Button>
                    </Link>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={logout}
                    className="text-xs px-2.5 lg:px-3 py-1.5 lg:py-2 whitespace-nowrap"
                  >
                    {t('nav.logout', 'Logout')}
                  </Button>
                </div>
              ) : (
                <div className={`flex items-center ${isRTL ? 'gap-x-reverse' : ''} gap-x-1.5 lg:gap-x-2`}>
                  <Link to="/login" onClick={scrollToTop}>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-xs px-2.5 lg:px-3 py-1.5 lg:py-2 whitespace-nowrap"
                    >
                      {t('nav.login', 'Login')}
                    </Button>
                  </Link>
                  <Link to="/register" onClick={scrollToTop}>
                    <Button 
                      size="sm" 
                      className="text-xs px-2.5 lg:px-3 py-1.5 lg:py-2 whitespace-nowrap"
                    >
                      {t('nav.register', 'Register')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Actions */}
            <div className={`flex md:hidden items-center ${isRTL ? 'gap-x-reverse' : ''} gap-x-1.5`}>
              
              {isAuthenticated ? (
                <>
                  {/* Notification - Mobile (Authenticated) */}
                  <button
                    onClick={() => { navigate('/notifications'); scrollToTop(); }}
                    className="p-2 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label={t('notifications.title', 'Notifications')}
                  >
                    <NotificationBell onClick={() => {}} />
                  </button>
                  
                  {/* Profile/Admin - Mobile (Authenticated) */}
                  <button
                    onClick={() => { 
                      navigate(user?.role === 'admin' ? '/admin' : '/profile'); 
                      scrollToTop(); 
                    }}
                    className="p-2 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label={user?.role === 'admin' ? t('nav.admin', 'Admin') : t('nav.profile', 'Profile')}
                  >
                    <UserIcon className="h-5 w-5" />
                  </button>
                  
                  {/* Logout - Mobile (Authenticated) */}
                  <button
                    onClick={handleLogout}
                    className="p-2 text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                    aria-label={t('nav.logout', 'Logout')}
                  >
                    <ArrowRightOnRectangleIcon className={`h-5 w-5 ${isRTL ? '' : 'rotate-180'}`} />
                  </button>
                </>
              ) : (
                <>
                  {/* Login Button - Mobile (Not Authenticated) */}
                  <button
                    onClick={() => { navigate('/login'); scrollToTop(); }}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label={t('nav.login', 'Login')}
                  >
                    {t('nav.login', 'Login')}
                  </button>
                  
                  {/* Register Button - Mobile (Not Authenticated) */}
                  <button
                    onClick={() => { navigate('/register'); scrollToTop(); }}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label={t('nav.register', 'Register')}
                  >
                    {t('nav.register', 'Register')}
                  </button>
                </>
              )}
              
              {/* Hamburger Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label={isMenuOpen ? t('nav.closeMenu', 'Close menu') : t('nav.openMenu', 'Open menu')}
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div 
            className="md:hidden border-t border-gray-200 bg-white"
            role="dialog"
            aria-label={t('nav.mobileMenu', 'Mobile navigation menu')}
          >
            <div className="px-2 pt-3 pb-4 space-y-1">
              
              {/* Navigation Links */}
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => { setIsMenuOpen(false); scrollToTop(); }}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    isActive(item.href)
                      ? 'text-primary-600 bg-primary-50 shadow-sm'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                  }`}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Divider */}
              <div className="pt-3 border-t border-gray-200" aria-hidden="true"></div>
              
              {/* Language Switcher */}
              <div className="flex items-center justify-center px-4 py-3">
                <ModernLanguageSwitcher />
              </div>
              
              {/* Cart - Mobile */}
              <button
                onClick={() => { setIsMenuOpen(false); navigate('/cart'); scrollToTop(); }}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <div className="flex items-center">
                  <ShoppingCartIcon className={`h-5 w-5 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                  <span>{t('cart.title', 'Cart')}</span>
                </div>
                {cartCount > 0 && (
                  <span className="bg-primary-500 text-white text-sm rounded-full h-6 min-w-[24px] px-2 flex items-center justify-center font-semibold">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </button>
              
              {/* Auth Actions - Mobile */}
              {isAuthenticated ? (
                <div className="pt-3 border-t border-gray-200 space-y-1">
                  {user?.role === 'admin' ? (
                    <Link
                      to="/admin"
                      onClick={() => { setIsMenuOpen(false); scrollToTop(); }}
                      className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {t('nav.admin', 'Admin Dashboard')}
                    </Link>
                  ) : (
                    <Link
                      to="/profile"
                      onClick={() => { setIsMenuOpen(false); scrollToTop(); }}
                      className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {t('nav.profile', 'Profile')}
                    </Link>
                  )}
                  
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-3 rounded-lg text-base font-medium text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    {t('nav.logout', 'Logout')}
                  </button>
                </div>
              ) : (
                <div className="pt-3 border-t border-gray-200 space-y-1">
                  <Link
                    to="/login"
                    onClick={() => { setIsMenuOpen(false); scrollToTop(); }}
                    className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {t('nav.login', 'Login')}
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => { setIsMenuOpen(false); scrollToTop(); }}
                    className="block px-4 py-3 rounded-lg text-base font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200 text-center focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {t('nav.register', 'Register')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
